'use client';

import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff } from 'lucide-react';
import { EXTERNAL_LINKS } from '@/lib/utils/external-links';
import { useTranslations } from 'next-intl';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { AuthBrandHeader } from './auth-brand-header';
import { AuthDivider } from './auth-divider';
import { AuthSsoButtons } from './auth-sso-buttons';
import { PasswordStrength } from '@/components/shared/password-strength';
import { signupSchema, type SignupInput } from '@/lib/schemas/auth/signup.schema';
import { useSignUp } from '@/hooks/mutations/auth/use-sign-up';
import { mapAuthError, type AuthErrorKey } from '@/lib/auth/auth-errors';
import { useFieldError } from '@/lib/i18n/use-field-error';
import { safeRedirect } from '@/lib/auth/safe-redirect';
import { cn } from '@/lib/utils/cn';
import { signupCampaignPartnerLogo, type SignupCampaign } from '@/lib/attribution/signup-campaign';
import type { Attribution } from '@/lib/schemas/attribution.schema';

interface AuthSignupFeatureProps {
  // Email validé transmis par la page depuis `?email=` (flow invite). '' si absent.
  prefilledEmail?: string;
  // `returnTo` brut depuis `?returnTo=` — sécurisé ici via `safeRedirect`.
  returnToRaw?: string | null;
  // Campagne d'acquisition reconnue par la page (UTM) : adapte l'en-tête.
  campaign?: SignupCampaign | null;
  // Attribution UTM (URL ou cookie) transmise à la metadata du signup.
  attribution?: Attribution | null;
}

export function AuthSignupFeature({
  prefilledEmail = '',
  returnToRaw = null,
  campaign = null,
  attribution = null,
}: AuthSignupFeatureProps) {
  const t = useTranslations('auth');
  const fieldError = useFieldError('auth');
  const router = useRouter();
  // `returnTo` survit jusqu'à la confirm email via `next` propagé dans
  // `emailRedirectTo` côté fetcher signUp (cf. lib/api/auth/sign-up.ts).
  const returnTo = safeRedirect(returnToRaw);
  const [confirmationSentTo, setConfirmationSentTo] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<AuthErrorKey | 'fallbackSignup' | null>(
    null,
  );

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { firstName: '', lastName: '', email: prefilledEmail, password: '' },
  });

  const password = useWatch({ control: form.control, name: 'password' });

  const signUpMutation = useSignUp();

  const onSubmit = form.handleSubmit((values) => {
    setSubmitErrorKey(null);
    signUpMutation.mutate(
      { ...values, returnTo, attribution },
      {
        onSuccess: (result) => {
          if (result.requiresEmailConfirmation) {
            setConfirmationSentTo(values.email);
            return;
          }
          // L'enrichissement (UTM, avatar, industry) se fait dans le layout SSR
          // de /onboarding — Server Action client KO en Next.js 16 dev (bug
          // forward / undici HEADERS_TIMEOUT). On redirige direct vers la racine,
          // le middleware route vers /onboarding tant que setup_done est false.
          // Si l'utilisateur arrivait depuis /invite/<token>, le auth callback
          // détectera l'invitation pending et ré-orientera correctement.
          router.replace(returnTo);
          router.refresh();
        },
        onError: (err) => {
          setSubmitErrorKey(mapAuthError(err) ?? 'fallbackSignup');
        },
      },
    );
  });

  if (confirmationSentTo) {
    return (
      <div className="flex flex-col gap-5">
        <AuthBrandHeader title={t('signup.emailSentTitle')} />
        <p className="text-fg-3 text-center text-sm">
          {t.rich('signup.emailSentBody', {
            email: confirmationSentTo,
            strong: (chunks) => <span className="text-fg-1 font-medium">{chunks}</span>,
          })}
        </p>
        <Button
          type="button"
          variant="mindexiaOutline"
          size="pill"
          className="w-full"
          onClick={() => setConfirmationSentTo(null)}
        >
          {t('signup.emailSentBack')}
        </Button>
      </div>
    );
  }

  const isPending = signUpMutation.isPending;

  return (
    <div className="flex flex-col">
      {campaign ? (
        <AuthBrandHeader
          partnerLogo={signupCampaignPartnerLogo(campaign)}
          title={t(`signup.campaigns.${campaign}.title`)}
          subtitle={t(`signup.campaigns.${campaign}.subtitle`)}
        />
      ) : (
        <AuthBrandHeader title={t('signup.title')} subtitle={t('signup.subtitle')} />
      )}

      <div className="mt-5 flex flex-col gap-3.5">
        <AuthSsoButtons mode="signup" />

        <AuthDivider />

        {submitErrorKey ? (
          <Alert
            variant="danger"
            onDismiss={() => setSubmitErrorKey(null)}
            dismissLabel={t('errors.dismiss')}
          >
            {t(`errors.${submitErrorKey}`)}
          </Alert>
        ) : null}

        <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <FloatingField
              label={t('fields.firstName')}
              autoComplete="given-name"
              placeholder={t('signup.firstNamePlaceholder')}
              error={fieldError(form.formState.errors.firstName?.message)}
              invalid={Boolean(form.formState.errors.firstName)}
              {...form.register('firstName')}
            />
            <FloatingField
              label={t('fields.lastName')}
              autoComplete="family-name"
              placeholder={t('signup.lastNamePlaceholder')}
              error={fieldError(form.formState.errors.lastName?.message)}
              invalid={Boolean(form.formState.errors.lastName)}
              {...form.register('lastName')}
            />
          </div>

          <FloatingField
            label={t('fields.email')}
            type="email"
            autoComplete="email"
            placeholder={t('signup.emailPlaceholder')}
            error={fieldError(form.formState.errors.email?.message)}
            invalid={Boolean(form.formState.errors.email)}
            {...form.register('email')}
          />

          <div>
            <FloatingField
              label={t('fields.password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={t('signup.passwordPlaceholder')}
              error={fieldError(form.formState.errors.password?.message)}
              invalid={Boolean(form.formState.errors.password)}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('password.hide') : t('password.show')}
                  className="text-fg-4 hover:text-fg-2 inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-150 hover:bg-neutral-100"
                >
                  {showPassword ? (
                    <EyeOff className="h-[18px] w-[18px]" />
                  ) : (
                    <Eye className="h-[18px] w-[18px]" />
                  )}
                </button>
              }
              {...form.register('password')}
            />
            {password && password.length > 0 ? <PasswordStrength value={password} /> : null}
          </div>

          <button
            type="submit"
            disabled={isPending}
            className={cn(
              'rounded-btn text-fg-on-brand mt-1.5 inline-flex h-11 items-center justify-center gap-2.5 px-5 text-[13px] font-semibold tracking-tight sm:h-[46px] sm:px-[22px] sm:text-sm',
              'shadow-brand transition-all duration-150 ease-out [background:var(--gradient-brand)]',
              'hover:-translate-y-px hover:brightness-105 active:translate-y-0',
              'focus-visible:ring-4 focus-visible:ring-purple-500/30 focus-visible:outline-none',
              'disabled:text-fg-5 disabled:transform-none disabled:cursor-progress disabled:shadow-none disabled:filter-none disabled:[background:var(--neutral-200)]',
            )}
          >
            {isPending ? (
              <span
                aria-hidden
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/45 border-t-white"
              />
            ) : null}
            <span>{isPending ? t('signup.submitLoading') : t('signup.submit')}</span>
          </button>

          <p className="text-fg-4 mt-2 text-center text-[11.5px] leading-[1.5]">
            {t.rich('signup.fineprint', {
              tos: (chunks) => (
                <a
                  href={EXTERNAL_LINKS.terms}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-fg-link font-medium hover:underline"
                >
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a
                  href={EXTERNAL_LINKS.privacy}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-fg-link font-medium hover:underline"
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
        </form>

        <div className="border-border-subtle text-fg-3 mt-[22px] flex items-center justify-center gap-1.5 border-t pt-[18px] text-[13.5px]">
          <span>{t('signup.toLoginPrompt')}</span>
          <Link
            href="/login"
            className="rounded-btn px-2.5 py-1.5 text-[13.5px] font-semibold text-purple-600 transition-colors duration-150 hover:bg-purple-50 hover:text-purple-700"
          >
            {t('signup.toLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
